import { lumi } from '@/lib/lumi'
import { useState, useEffect } from 'react'
import { sanitizeAgentList } from '@/utils/agentSecurity'

export interface Agent {
  _id?: string
  name: string
  walletAddress: string
  category: string
  capabilities: string[]
  pricePerTask: number
  pricingModel: 'per_task' | 'per_hour' | 'subscription'
  availability: 'online' | 'busy' | 'offline'
  responseTimeSLA: string
  supportedFormats: string[]
  apiEndpoint: string
  rating?: number
  tasksCompleted?: number
  totalEarnings?: number
  stakeAmount?: number
  isAutonomous?: boolean
  apiKey?: string
  lastActive?: string
  autonomousConfig?: {
    autoClaimTasks: boolean
    categoryFilter: string[]
    minBudget: number
    maxBudget: number
  }
  creator: string
  createdAt: string
  updatedAt: string
}

export function useAgents() {
  const [agents, setAgents] = useState<Agent[]>([])
  const [loading, setLoading] = useState(false)

  const fetchAgents = async (filter?: any) => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.agents.list({
        filter: filter || {},
        sort: { rating: -1 }
      })
      // SECURITY: Sanitize agent data to remove sensitive fields
      const sanitizedAgents = sanitizeAgentList(list)
      // Filter out deleted accounts
      const activeAgents = sanitizedAgents.filter(agent => !agent.name.startsWith('[DELETED]'))
      setAgents(activeAgents)
      console.log('agents fetched (sanitized):', sanitizedAgents.length)
    } catch (error) {
      console.error('Fetch agents failed:', error)
    } finally {
      setLoading(false)
    }
  }

  const createAgent = async (agentData: Partial<Agent>) => {
    try {
      const agent = await lumi.entities.agents.create(agentData)
      console.log('agent created:', agent)
      await fetchAgents()
      return agent
    } catch (error) {
      console.error('Create agent failed:', error)
      throw error
    }
  }

  const updateAgent = async (agentId: string, updates: Partial<Agent>) => {
    try {
      await lumi.entities.agents.update(agentId, updates)
      console.log('agent updated:', agentId)
      await fetchAgents()
    } catch (error) {
      console.error('Update agent failed:', error)
      throw error
    }
  }

  useEffect(() => {
    fetchAgents()
  }, [])

  return { agents, loading, fetchAgents, createAgent, updateAgent }
}
